/**
 * Celebrates the moment a cart reward milestone is newly crossed.
 *
 * The progress bar re-renders on every cart change, so "is milestone 1 reached"
 * is not enough — that would replay the animation every time the drawer opens.
 * We track the last level seen in sessionStorage and only fire when it goes UP.
 *
 * Level is read from `data-level` on .cart-progress: 0 = none, 1 = first
 * milestone, 2 = both. An absent .cart-progress means an empty cart, which
 * resets the level to 0 so the next qualifying add celebrates again.
 */

const STORAGE_KEY = 'cgs:cart-progress-level';
const CELEBRATION_MS = 1400;

function readLevel() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw === null ? -1 : parseInt(raw, 10);
  } catch (e) {
    return -1;
  }
}

function writeLevel(level) {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(level));
  } catch (e) {
    /* private mode — celebrate once per render at worst */
  }
}

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function spark(node) {
  const layer = document.createElement('span');
  layer.className = 'cart-progress__sparks';

  for (let i = 0; i < 12; i++) {
    const particle = document.createElement('i');
    const angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.35;
    const distance = 13 + Math.random() * 15;
    particle.style.setProperty('--dx', `${(Math.cos(angle) * distance).toFixed(1)}px`);
    particle.style.setProperty('--dy', `${(Math.sin(angle) * distance).toFixed(1)}px`);
    particle.style.setProperty('--spark-delay', `${Math.round(Math.random() * 90)}ms`);
    layer.appendChild(particle);
  }

  node.appendChild(layer);
  setTimeout(() => layer.remove(), 1200);
}

function celebrate(el, level) {
  if (el.dataset.celebrate === 'false') return;
  if (prefersReducedMotion()) return;

  // Restart the CSS animations even if a celebration is already running.
  el.classList.remove('is-celebrating');
  void el.offsetWidth;
  el.classList.add('is-celebrating');

  const node =
    level >= 2
      ? el.querySelector('.cart-progress__node--end')
      : el.querySelector('.cart-progress__node:not(.cart-progress__node--end)') ||
        el.querySelector('.cart-progress__node--end');

  if (node) {
    node.classList.remove('is-popping');
    void node.offsetWidth;
    node.classList.add('is-popping');
    spark(node);
    setTimeout(() => node.classList.remove('is-popping'), CELEBRATION_MS);
  }

  if (navigator.vibrate) {
    try {
      navigator.vibrate(18);
    } catch (e) {
      /* no haptics available */
    }
  }

  setTimeout(() => el.classList.remove('is-celebrating'), CELEBRATION_MS);
}

function check() {
  const el = document.querySelector('.cart-progress');

  if (!el) {
    // No bar rendered = empty cart (or the feature is off). Reset so the next
    // qualifying cart celebrates rather than being swallowed as "already there".
    writeLevel(0);
    return;
  }

  const level = parseInt(el.dataset.level || '0', 10);
  if (Number.isNaN(level)) return;

  const previous = readLevel();

  // First time we've ever seen the bar this session: record, don't celebrate.
  // Otherwise a returning customer gets fireworks just for opening the drawer.
  if (previous === -1) {
    writeLevel(level);
    return;
  }

  if (level > previous) celebrate(el, level);
  if (level !== previous) writeLevel(level);
}

let pending;
function scheduleCheck() {
  clearTimeout(pending);
  pending = setTimeout(check, 120);
}

check();

// The cart drawer and cart page are re-rendered via the Section Rendering API,
// which replaces the progress markup wholesale — so watch for DOM changes
// rather than hooking a specific cart event. The callback is debounced and does
// a single querySelector, so the cost is negligible.
new MutationObserver(scheduleCheck).observe(document.documentElement, {
  childList: true,
  subtree: true,
});

document.addEventListener('cart:update', scheduleCheck);
